import {fetchData} from "../Service/graphQlService";
import {GET_STATS_BY_STATUS, GET_STATS_BY_TYPE} from "../Graphql/Queries/HscStatsQuery";

/**
 * Every closed-transaction figure the deck prints: the four along the foot of page 05,
 * the two on page 08, and all of page 09.
 *
 * These use the closed columns that already existed rather than any BOV-specific ones, so
 * the deck reports the same closed count as the rest of the app:
 *
 *   statsByStatus.CLOSED_IS_PROPERTIES / _PRICE / _SQFT   combined totals
 *   statsByType.CLOSED_MT_COUNT                           the multi-tenant split, page 05
 *
 * All of those select from the unique_props CTE, whose population is
 *
 *   DELETED IS NULL
 *   AND (SOURCE = 'STRIVE'
 *        OR SOURCE = 'External' AND STRIVE was the buyer-side firm)
 *
 * with STATUS = 'Closed' applied by the column. So the totals include External deals
 * STRIVE bought on, which is why they read 1,757 rather than the 1,745 a
 * source=STRIVE-only rule gives.
 *
 * Two things to know:
 *
 *  - The CLOSED_IS_* family ignores SINCE; the plain CLOSED_* columns are bounded by it.
 *    The deck wants every figure all-time, so it reads the CLOSED_IS_* names. They return
 *    the same values as CLOSED_* here only because SINCE is passed as 1900-01-01, and
 *    reading CLOSED_IS_* means that stays true if the bound is ever used for real. SINCE
 *    still has to be passed because the query interpolates it.
 *
 *  - Because of the CTE's source rule, these totals cannot be reconciled by filtering the
 *    properties list on status=Closed + source=STRIVE - that filter reports 12 fewer. Worth
 *    knowing if anyone tries to tie the printed count back to a list view.
 */

// Required by the query but bounds nothing this module reads.
const UNUSED_SINCE = '1900-01-01';

/** Asset classes in the order page 09 prints them, with their PROPERTIES.CATEGORY key. */
const ASSET_CLASSES = [
    {name: 'Retail', key: 'RETAIL'},
    {name: 'Office', key: 'OFFICE'},
    {name: 'Industrial', key: 'INDUSTRIAL'},
    {name: 'Multi-Family', key: 'MF'},
];

/**
 * Money, formatted as the template writes it: "$6.8B+", "$578M+".
 * The template appends "+" to value figures but not to counts.
 */
export function formatVolume(value) {
    const n = Number(value);
    if (!isFinite(n) || n <= 0) return '-';
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B+`;
    if (n >= 1e6) return `$${Math.round(n / 1e6)}M+`;
    if (n >= 1e3) return `$${Math.round(n / 1e3)}K+`;
    return `$${Math.round(n)}`;
}

/**
 * Page 09 writes volumes without the "+" and keeps significant decimals rather than
 * rounding to one: the template prints "$5.6B", "$6.38B", "$578M", "$49M".
 */
export function formatVolumePlain(value) {
    const n = Number(value);
    if (!isFinite(n) || n <= 0) return '-';
    if (n >= 1e9) return `$${trimZeros((n / 1e9).toFixed(2))}B`;
    if (n >= 1e6) return `$${Math.round(n / 1e6)}M`;
    if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
    return `$${Math.round(n)}`;
}

/** Square feet, as page 09 writes it: "45.5M". */
export function formatSqft(value) {
    const n = Number(value);
    if (!isFinite(n) || n <= 0) return '-';
    if (n >= 1e6) return `${trimZeros((n / 1e6).toFixed(1))}M`;
    if (n >= 1e3) return `${trimZeros((n / 1e3).toFixed(1))}K`;
    return Math.round(n).toLocaleString('en-US');
}

function trimZeros(text) {
    return String(text).replace(/\.?0+$/, '');
}

/** First argument that is a usable number. */
function pick(...values) {
    for (const value of values) {
        const n = Number(value);
        if (isFinite(n) && n > 0) return n;
    }
    return null;
}

/** Counts, comma-grouped and with no suffix: "578", "1,750". */
export function formatCount(value) {
    const n = Number(value);
    if (!isFinite(n) || n <= 0) return '-';
    return Math.round(n).toLocaleString('en-US');
}

function firstRow(payload) {
    return (payload && payload.data && payload.data[0]) || {};
}

/**
 * Returns the page 05 figures, plus the two on page 08 that are the same metric.
 * Anything neither query can supply is left as a dash by the formatters.
 */
export async function loadBovClosings() {
    // statsByType is fetched only for CLOSED_MT_COUNT, the multi-tenant count on page 05.
    // It selects from the same CTE as the totals, so the two agree.
    const [res, typeRes] = await Promise.all([
        fetchData(GET_STATS_BY_STATUS, {since: UNUSED_SINCE}),
        fetchData(GET_STATS_BY_TYPE),
    ]);
    const status = firstRow(res && res.data && res.data.statsByStatus);
    const byType = firstRow(typeRes && typeRes.data && typeRes.data.statsByType);

    const allCount = pick(status.CLOSED_IS_PROPERTIES);
    const allValue = pick(status.CLOSED_IS_PRICE);
    const mtCount = pick(byType.CLOSED_MT_COUNT);
    const mtValue = pick(status.CLOSED_IS_MT_PRICE);
    // The single-tenant pair, for the Capital Flows page of a single-tenant deck. The count
    // is an existing column (statsByType, P.TYPE = 'STNL'); the value needs
    // CLOSED_IS_STNL_PRICE, added alongside CLOSED_IS_MT_PRICE in strive-keystone, and reads
    // as a dash until that is deployed.
    const stCount = pick(byType.CLOSED_STNL_COUNT);
    const stValue = pick(status.CLOSED_IS_STNL_PRICE);
    // The Multi-Family pair, for page 05 of a Multi-Family deck. Both are columns that already
    // exist: the class figures page 09 reads, falling back to statsByType's CLOSED_MF_COUNT
    // for the count, so this adds nothing new to strive-keystone.
    const mfCount = pick(status.CLOSED_IS_MF_PROPERTIES, byType.CLOSED_MF_COUNT);
    const mfValue = pick(status.CLOSED_IS_MF_PRICE);

    return {
        // Capital Flows. Both tenancy pairs are returned and the page picks the one that
        // matches the deck - see TENANCY_FIGURES in CapitalFlows.
        multiTenantValueSold: formatVolume(mtValue),
        multiTenantPropertiesSold: formatCount(mtCount),
        singleTenantValueSold: formatVolume(stValue),
        singleTenantPropertiesSold: formatCount(stCount),
        multiFamilyValueSold: formatVolume(mfValue),
        multiFamilyPropertiesSold: formatCount(mfCount),
        totalValueSold: formatVolume(allValue),
        totalPropertiesSold: formatCount(allCount),

        // page 08 - the same two all-time figures, where the template does suffix the
        // count as well ("1,750+ closings").
        closings: allCount > 0 ? `${formatCount(allCount)}+` : '-',
        aggregateVolume: formatVolume(allValue),

        // page 09. The class figures sum below the total for two reasons: the total has no
        // CATEGORY filter, so closings whose category is 'other' or null land in none of
        // the template's four columns (5 deals), and the class columns still apply
        // SOURCE = 'STRIVE' where the total no longer does (12 deals). 17 in total.
        // Closing that gap needs per-class figures on the same rule as the total - see the
        // note on the class columns below.
        totalClosings: formatCount(status.CLOSED_IS_PROPERTIES),
        totalVolume: formatVolumePlain(status.CLOSED_IS_PRICE),
        totalSqftSold: formatSqft(status.CLOSED_IS_SQFT),
        // The class split is the one thing no pre-existing column carries: nothing outside
        // the BOV work breaks closings down by CATEGORY, bar statsByType.CLOSED_MF_COUNT,
        // which is a count with no value. So these eight and CLOSED_IS_MT_PRICE above are
        // the only columns added for the deck, and they apply the same rule as the totals
        // rather than a second definition of "closed". They render as dashes until the
        // statsByStatus change ships.
        assetClasses: ASSET_CLASSES.map(assetClass => ({
            name: assetClass.name,
            closings: formatCount(status[`CLOSED_IS_${assetClass.key}_PROPERTIES`]),
            volume: formatVolumePlain(status[`CLOSED_IS_${assetClass.key}_PRICE`]),
        })),
    };
}
